/**
 * Gedeelde "Leden & uitnodigingen"-flow (Mastermind + netwerkclub profile hub).
 * Firestore-rol `organizer` wordt in de UI als beheerder getoond; capabilities alleen bij organizer.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { searchUsers } from '../../services/userSearchService';
import {
  collection,
  addDoc,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
  type UpdateData,
} from 'firebase/firestore';
import { NetworkClubMember } from '../../types/networkclub';
import type { EntitySummary } from '../../hooks/useEntityContext';
import {
  CAPABILITY_KEYS,
  DEFAULT_ORGANIZER_CAPABILITIES,
  type EntityMemberCapabilities,
  strictCapabilities,
} from '../../types/entityMemberCapabilities';
import { Loader } from '../icons';
import { CheckIcon, MailIcon, InfoIcon } from '../icons/phosphorSystem';
import { withAlpha } from '../../utils/colorUtils';

export type ClubInviteMemberRow = NetworkClubMember & {
  country?: string;
  timezone?: string;
  capabilities?: Partial<EntityMemberCapabilities> | null;
};

type InviteRole = 'member' | 'organizer' | 'board' | 'va';

const CAP_LABELS: Record<keyof EntityMemberCapabilities, string> = {
  canStartMeeting: 'Meeting',
  canManageOverlays: 'Presentatie',
  canManageGuests: 'Gasten',
  canInviteMembers: 'Uitnodigen',
  canEditBranding: 'Huisstijl',
  canManageAgenda: 'Agenda',
  canManageGrowth: 'Groei',
  canManageSessions: 'Sessies',
};

function memberRowEditable(
  isOwner: boolean,
  isBoard: boolean,
  canInviteMembersHub: boolean,
  target: ClubInviteMemberRow,
): boolean {
  if (target.role === 'owner') return false;
  if (target.role === 'board') return isOwner || isBoard;
  return isOwner || isBoard || canInviteMembersHub;
}

function roleLabelHub(role: NetworkClubMember['role']): string {
  if (role === 'owner') return 'Eigenaar';
  if (role === 'board') return 'Bestuur';
  if (role === 'organizer') return 'Beheerder';
  if (role === 'va') return 'VA';
  return 'Lid';
}

function inviteTargetLabel(r: InviteRole): string {
  if (r === 'organizer') return 'beheerder';
  if (r === 'board') return 'bestuurslid';
  if (r === 'va') return 'VA';
  return 'lid';
}

type MemberRole = NetworkClubMember['role'];

const CAPABILITY_ROWS: readonly (readonly [keyof EntityMemberCapabilities, string, string])[] = [
  ['canStartMeeting', 'Meetings starten', 'Countdown, pre-meeting, Jitsi-host'],
  ['canManageOverlays', 'Presentatie & overlays', 'Feature switches en overlay in de meeting'],
  ['canManageGuests', 'Gasten', 'Gast-spotlight en gerelateerde opties'],
  ['canInviteMembers', 'Leden uitnodigen', 'Uitnodigingsflow en ledenpaneel'],
  ['canEditBranding', 'Huisstijl', 'Cover en logo op de hub'],
  ['canManageAgenda', 'Agenda', 'Meetings plannen en agenda-tab'],
  ['canManageGrowth', 'Groei & doelen', 'Structured Growth Engine (doelen, voorbereiding)'],
  ['canManageSessions', 'Sessies & AI-notities', 'Sessiedetails, template in pre-meeting, AI-notes-blok'],
];

type HubGuideSection = { heading: string; paragraphs: string[] };

function getHubGuide(entityType: EntitySummary['type'] | undefined): { title: string; intro: string; sections: HubGuideSection[] } {
  if (entityType === 'networkclub') {
    return {
      title: 'Hoe werkt deze netwerkclub-hub?',
      intro:
        'Kort overzicht van de club-omgeving. Let op: dat is iets anders dan de vinkjes hieronder. Die geven alleen aan wat een beheerder wel of niet mag doen.',
      sections: [
        {
          heading: 'In het kort',
          paragraphs: [
            'Je hub brengt leden, uitnodigingen, profiel en meetings samen. Iedereen met toegang ziet wat bij jullie club hoort.',
          ],
        },
        {
          heading: 'Rollen',
          paragraphs: [
            'Eigenaar en bestuur hebben brede regie. Een beheerder stel je verder af met de vinkjes: zo geef je alleen de rechten die passen bij die persoon.',
          ],
        },
        {
          heading: 'Meetings',
          paragraphs: [
            'Vanuit de hub start of join je vaak een online meeting (soms met countdown). Wie dat mag, hangt af van rol en vinkjes.',
          ],
        },
        {
          heading: 'Agenda & planning',
          paragraphs: [
            'In de agenda-tab plan je afspraken; in de header zie je vaak een snelle “next meeting”-route.',
          ],
        },
      ],
    };
  }

  return {
    title: 'Hoe werkt je Mastermind of community?',
    intro:
      'Dit is het grote plaatje van de groep. De vinkjes hieronder zeggen alleen per beheerder wat hij of zij mag. Ze leggen niet uit hoe de hele hub in elkaar zit.',
    sections: [
      {
        heading: 'Waarvoor dient dit?',
        paragraphs: [
          'Een vaste groep die samen online meet, groeit en afspraken vastlegt, zoals sessies, doelen, agenda en nabeschouwing. Dat gebeurt allemaal op één plek.',
        ],
      },
      {
        heading: 'Leden uitnodigen',
        paragraphs: [
          'Via link of gerichte uitnodiging. Je kiest lid of beheerder. Bij beheerder bepalen de vinkjes welke onderdelen iemand mag gebruiken (agenda, groei, sessies, huisstijl, enz.).',
        ],
      },
      {
        heading: 'Flow van een sessie',
        paragraphs: [
          'De host start de meeting via de hub: eerst countdown en pre-meeting, daarna de videocall. Leden kunnen meekijken of meedoen.',
          'Na afloop kun je reflectie invullen. Opnames en samenvattingen vind je terug onder het tabblad Sessies.',
        ],
      },
      {
        heading: 'Tabs in de hub',
        paragraphs: [
          'Bij Sessies zie je de geschiedenis, opnames en AI-notities.',
          'Groei is er voor voorbereiding, doelen, commitments en hoe de groep ervoor staat.',
          'Onder Agenda plan je de volgende meetings.',
          'Instellingen is voor praktische zaken: info over de groep, groep verlaten, en cover en logo.',
        ],
      },
      {
        heading: 'Next meeting',
        paragraphs: [
          'In de header zie je wanneer de volgende meeting is; je kunt doorlinken naar plannen of deelnemen als er iets gepland staat.',
        ],
      },
    ],
  };
}

function capsEqual(a: EntityMemberCapabilities, b: EntityMemberCapabilities): boolean {
  return CAPABILITY_KEYS.every((k) => a[k] === b[k]);
}

function buildMemberUpdatePatch(
  m: ClubInviteMemberRow,
  nextRole: MemberRole,
  nextCaps: EntityMemberCapabilities,
): Record<string, unknown> {
  const prevRole = m.role;
  const patch: Record<string, unknown> = {};
  if (nextRole !== prevRole) patch.role = nextRole;
  if (nextRole === 'organizer') {
    const prevCaps =
      prevRole === 'organizer'
        ? strictCapabilities((m.capabilities || {}) as Partial<EntityMemberCapabilities>)
        : null;
    const needCaps =
      prevRole !== 'organizer' || prevCaps == null || !capsEqual(prevCaps, nextCaps);
    if (needCaps) patch.capabilities = nextCaps;
  } else if (prevRole === 'organizer') {
    patch.capabilities = deleteField();
  }
  return patch;
}

function canSelectMemberForEdit(
  m: ClubInviteMemberRow,
  isOwner: boolean,
  isBoard: boolean,
  canInviteMembersHub: boolean,
): boolean {
  if (m.role === 'owner') return false;
  if (!memberRowEditable(isOwner, isBoard, canInviteMembersHub, m)) return false;
  if (!isOwner && !isBoard && (m.role === 'board' || m.role === 'va')) return false;
  return true;
}

export type ClubInviteMembersModalProps = {
  clubName: string;
  entityId: string;
  clubId: string;
  userId: string;
  userName: string;
  userRole?: string;
  entityType?: EntitySummary['type'];
  members: ClubInviteMemberRow[];
  isOwner: boolean;
  isBoard: boolean;
  canInviteMembersHub: boolean;
  onMembersChanged: () => void;
  /** Inline-variant: mag leeg; sluitknop in header wordt dan verborgen. */
  onClose?: () => void;
  accentColor?: string;
  variant?: 'modal' | 'inline';
  /** Alleen netwerkclub-hub: extra rollen zonder capability-map. */
  inviteRoleExtras?: readonly ('board' | 'va')[];
};

export const ClubInviteMembersModal: React.FC<ClubInviteMembersModalProps> = ({
  clubName,
  entityId,
  clubId,
  userId,
  userName,
  userRole,
  entityType = 'mastermind',
  members,
  isOwner,
  isBoard,
  canInviteMembersHub,
  onMembersChanged,
  onClose,
  accentColor = '#8DA8A0',
  variant = 'modal',
  inviteRoleExtras,
}) => {
  const [role, setRole] = useState<InviteRole>('member');
  const [capabilities, setCapabilities] = useState<EntityMemberCapabilities>(() => ({
    ...DEFAULT_ORGANIZER_CAPABILITIES,
  }));
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [msgCopied, setMsgCopied] = useState(false);
  const [managingUid, setManagingUid] = useState<string | null>(null);

  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<MemberRole>('member');
  const [editCapabilities, setEditCapabilities] = useState<EntityMemberCapabilities>(() => ({
    ...DEFAULT_ORGANIZER_CAPABILITIES,
  }));
  const [editSaving, setEditSaving] = useState(false);
  const [hubGuideOpen, setHubGuideOpen] = useState(false);

  const selectedMember = useMemo(
    () => (selectedMemberId ? members.find((x) => x.id === selectedMemberId) : undefined),
    [members, selectedMemberId],
  );

  const memberEditPatch = useMemo(() => {
    if (!selectedMember) return {};
    return buildMemberUpdatePatch(selectedMember, editRole, editCapabilities);
  }, [selectedMember, editRole, editCapabilities]);

  const isMemberEditDirty = Object.keys(memberEditPatch).length > 0;

  const showBoardVaInEditor = isOwner || isBoard;

  const hubGuide = useMemo(() => getHubGuide(entityType), [entityType]);

  useEffect(() => {
    if (!hubGuideOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHubGuideOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hubGuideOpen]);

  useEffect(() => {
    if (selectedMemberId && !members.some((m) => m.id === selectedMemberId)) {
      setSelectedMemberId(null);
    }
  }, [members, selectedMemberId]);

  const pickEditRole = useCallback(
    (r: MemberRole) => {
      if (r === 'organizer') {
        const m = members.find((x) => x.id === selectedMemberId);
        if (m?.role === 'organizer') {
          setEditCapabilities(
            strictCapabilities((m.capabilities || {}) as Partial<EntityMemberCapabilities>),
          );
        } else {
          setEditCapabilities({ ...DEFAULT_ORGANIZER_CAPABILITIES });
        }
      }
      setEditRole(r);
    },
    [members, selectedMemberId],
  );

  const openMemberEditor = useCallback((m: ClubInviteMemberRow) => {
    setSelectedMemberId(m.id);
    setEditRole(m.role);
    if (m.role === 'organizer') {
      setEditCapabilities(
        strictCapabilities((m.capabilities || {}) as Partial<EntityMemberCapabilities>),
      );
    } else {
      setEditCapabilities({ ...DEFAULT_ORGANIZER_CAPABILITIES });
    }
  }, []);

  const toggleEditCapability = (key: keyof EntityMemberCapabilities) => {
    setEditCapabilities((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearMemberSelection = () => setSelectedMemberId(null);

  const T = useMemo(
    () => ({
      bg: '#FAFAF9',
      white: '#FFFFFF',
      border: '#E5E5EA',
      borderLight: '#F0F0F0',
      text: '#1C1C1E',
      textSub: '#6B6B6B',
      textMuted: '#ABABAB',
      accent: accentColor,
      accentLight: withAlpha(accentColor, 0.14),
      accentBg: withAlpha(accentColor, 0.1),
      badgeDark: '#1C1C1E',
      badgeLight: '#F0F0F0',
      green: accentColor,
      red: '#EF4444',
      shadow: '0 2px 12px rgba(0, 0, 0, 0.07)',
      shadowSm: '0 1px 4px rgba(0, 0, 0, 0.06)',
    }),
    [accentColor],
  );

  const S = useMemo(
    () =>
      ({
        card: {
          background: T.white,
          borderRadius: 14,
          border: `1px solid ${T.border}`,
          boxShadow: T.shadowSm,
        } as React.CSSProperties,
        headerBtn: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 34,
          padding: '0 14px',
          borderRadius: 20,
          background: T.white,
          border: `1px solid ${T.border}`,
          fontSize: 13,
          fontWeight: 600,
          color: T.text,
          cursor: 'pointer',
          whiteSpace: 'nowrap' as const,
          transition: 'background 0.15s',
        } as React.CSSProperties,
        accentBtn: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 34,
          padding: '0 16px',
          borderRadius: 20,
          background: T.accent,
          border: 'none',
          fontSize: 13,
          fontWeight: 700,
          color: '#fff',
          cursor: 'pointer',
        } as React.CSSProperties,
      }) as const,
    [T],
  );

  useEffect(() => {
    if (role === 'organizer') setCapabilities({ ...DEFAULT_ORGANIZER_CAPABILITIES });
  }, [role]);

  const isStaffUI = userRole === 'organizer' || userRole === 'owner' || userRole === 'board';

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://superkonnected.com';
  const instapLink = `${origin}/?onboarding=1&inviteEntity=${entityId}&inviteBy=${userId}`;
  const fullMsg = `Sla nu je notities gratis op, word gratis lid van SuperKonnected en free your mind.\n\nStart hier: ${instapLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(instapLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      /* ignore */
    }
  };
  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(fullMsg);
      setMsgCopied(true);
      setTimeout(() => setMsgCopied(false), 2500);
    } catch {
      /* ignore */
    }
  };

  const handleSearch = async (v: string) => {
    setSearch(v);
    if (v.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      setSearchResults(await searchUsers(v, 6));
    } catch {
      setSearchResults([]);
    }
    setSearching(false);
  };

  const sendInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'entityInvitations'), {
        entityId,
        clubId,
        clubName,
        entityName: clubName,
        entityType,
        invitedBy: userId,
        inviterName: userName,
        invitedEmail: email.trim().toLowerCase(),
        instapLink,
        role,
        ...(role === 'organizer' ? { capabilities } : {}),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setSent(true);
      setTimeout(() => {
        setSent(false);
        setEmail('');
      }, 3000);
    } finally {
      setSending(false);
    }
  };

  const toggleCapability = (key: keyof EntityMemberCapabilities) => {
    setCapabilities((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const memberDocRef = (uid: string) => doc(db, 'entityMembers', `${entityId}_${uid}`);

  const saveMemberEdits = async () => {
    if (!selectedMember || !isMemberEditDirty) return;
    const roleNl = roleLabelHub(editRole);
    if (
      !window.confirm(
        `Wijzigingen opslaan voor ${selectedMember.name}?\n\n` +
          `Nieuwe rol: ${roleNl}` +
          (editRole === 'organizer' ? ' (rechten zoals hieronder).' : '.'),
      )
    ) {
      return;
    }
    setEditSaving(true);
    try {
      await updateDoc(memberDocRef(selectedMember.id), memberEditPatch as UpdateData<ClubInviteMemberRow>);
      onMembersChanged();
      clearMemberSelection();
    } catch (e) {
      console.error(e);
      alert('Opslaan mislukt. Controleer je rechten en probeer opnieuw.');
    } finally {
      setEditSaving(false);
    }
  };

  const removeMemberFromEntity = async (m: ClubInviteMemberRow) => {
    if (!window.confirm(`${m.name} volledig uit deze groep verwijderen?`)) return;
    setManagingUid(m.id);
    try {
      await deleteDoc(memberDocRef(m.id));
      onMembersChanged();
    } catch (e) {
      console.error(e);
      alert('Kon lid niet verwijderen.');
    } finally {
      setManagingUid(null);
    }
  };

  const renderQuickInvite = (extraStyle?: React.CSSProperties) => (
    <div style={{ background: T.bg, borderRadius: 12, padding: '14px 16px', marginBottom: 12, ...extraStyle }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          readOnly
          value={instapLink}
          style={{
            flex: 1,
            height: 36,
            padding: '0 10px',
            borderRadius: 9,
            border: `1px solid ${T.border}`,
            fontSize: 11.5,
            color: T.textSub,
            background: T.white,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => void copyLink()}
          style={{ ...S.accentBtn, height: 36, fontSize: 12, background: linkCopied ? T.green : T.accent }}
        >
          {linkCopied ? (
            <>
              <CheckIcon size={13} weight="bold" /> Gekopieerd
            </>
          ) : (
            'Kopieer link'
          )}
        </button>
      </div>
      <button
        type="button"
        onClick={() => void copyMsg()}
        style={{
          width: '100%',
          height: 38,
          borderRadius: 9,
          border: `1.5px solid ${T.border}`,
          background: T.white,
          fontSize: 12.5,
          fontWeight: 600,
          color: T.text,
          cursor: 'pointer',
          marginBottom: 8,
        }}
      >
        {msgCopied ? (
          <>
            <CheckIcon size={13} weight="bold" /> Bericht gekopieerd
          </>
        ) : (
          'Kopieer uitnodigingsbericht'
        )}
      </button>
      <div style={{ display: 'flex', gap: 8 }}>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(fullMsg)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            height: 38,
            borderRadius: 9,
            border: `1.5px solid ${T.border}`,
            background: T.white,
            fontSize: 12.5,
            fontWeight: 600,
            color: T.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
          }}
        >
          WhatsApp
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(instapLink)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            height: 38,
            borderRadius: 9,
            border: `1.5px solid ${T.border}`,
            background: T.white,
            fontSize: 12.5,
            fontWeight: 600,
            color: T.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
          }}
        >
          LinkedIn
        </a>
        <a
          href={`mailto:?subject=${encodeURIComponent('Uitnodiging SuperKonnected')}&body=${encodeURIComponent(fullMsg)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            height: 38,
            borderRadius: 9,
            border: `1.5px solid ${T.border}`,
            background: T.white,
            fontSize: 12.5,
            fontWeight: 600,
            color: T.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
          }}
        >
          Mail
        </a>
      </div>
    </div>
  );

  const extras = inviteRoleExtras?.length ? [...new Set(inviteRoleExtras)] : [];
  const showRoleExtras = extras.length > 0;

  /** Modal: vaste afmetingen zodat de kaart niet springt bij wisselen lid / minder inhoud. */
  const modalCardHeight = 'min(93vh, 960px)';

  const cardOuter: React.CSSProperties =
    variant === 'inline'
      ? {
          width: '100%',
          maxWidth: '100%',
          maxHeight: 'none',
          minHeight: 'min(64vh, 680px)',
          background: T.white,
          borderRadius: 14,
          border: `1px solid ${T.border}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }
      : {
          width: 'min(1040px, calc(100vw - 32px))',
          maxWidth: 1040,
          height: modalCardHeight,
          minHeight: modalCardHeight,
          maxHeight: modalCardHeight,
          background: T.white,
          borderRadius: 22,
          boxShadow: '0 16px 56px rgba(0,0,0,0.22)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        };

  const rightsBlockHeader = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 10,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          color: T.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
          flex: 1,
          minWidth: 0,
        }}
      >
        Rechten van deze beheerder
      </div>
      <button
        type="button"
        title="Hoe de hub in zijn geheel werkt (niet de losse vinkjes)"
        onClick={() => setHubGuideOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 28,
          padding: '0 10px',
          borderRadius: 8,
          border: `1px solid ${T.border}`,
          background: T.white,
          fontSize: 11,
          fontWeight: 700,
          color: T.accent,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <InfoIcon size={14} weight="regular" />
        Hoe werkt dit?
      </button>
    </div>
  );

  const hubGuidePanel = hubGuideOpen ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sk-hub-guide-title"
      onClick={() => setHubGuideOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 260,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, calc(100vw - 24px))',
          maxHeight: 'min(88vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
          background: T.white,
          borderRadius: 16,
          border: `1px solid ${T.border}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '14px 16px',
            borderBottom: `1px solid ${T.border}`,
            flexShrink: 0,
          }}
        >
          <div id="sk-hub-guide-title" style={{ fontSize: 15, fontWeight: 800, color: T.text, lineHeight: 1.25 }}>
            {hubGuide.title}
          </div>
          <button
            type="button"
            aria-label="Sluiten"
            onClick={() => setHubGuideOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.textMuted,
              fontSize: 22,
              lineHeight: 1,
              padding: '4px 8px',
            }}
          >
            ×
          </button>
        </div>
        <div
          className="scrollbar-always"
          style={{
            flex: 1,
            minHeight: 120,
            overflowY: 'auto',
            padding: '16px 18px 18px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <p style={{ fontSize: 12.5, color: T.textSub, lineHeight: 1.55, margin: '0 0 18px' }}>{hubGuide.intro}</p>
          {hubGuide.sections.map((sec, i) => (
            <div
              key={i}
              style={{
                marginBottom: i === hubGuide.sections.length - 1 ? 0 : 18,
                paddingBottom: i === hubGuide.sections.length - 1 ? 0 : 16,
                borderBottom: i === hubGuide.sections.length - 1 ? 'none' : `1px solid ${T.borderLight}`,
              }}
            >
              <div style={{ fontSize: 12.5, fontWeight: 800, color: T.accent, marginBottom: 8, letterSpacing: 0.2 }}>
                {sec.heading}
              </div>
              {sec.paragraphs.map((p, j) => (
                <p key={j} style={{ fontSize: 13, color: T.text, lineHeight: 1.55, margin: j === 0 ? 0 : '10px 0 0' }}>
                  {p}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  const inner = (
    <div style={cardOuter}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 22px 14px',
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: T.text, margin: 0 }}>Leden & uitnodigingen</h3>
          <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 4 }}>{clubName}</div>
          {isStaffUI ? (
            <button
              type="button"
              onClick={() => setHubGuideOpen(true)}
              title="Uitleg: hoe de hub in zijn geheel werkt"
              style={{
                marginTop: 8,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: T.accent,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              Hoe werkt deze groep?
            </button>
          ) : null}
        </div>
        {variant === 'modal' && onClose ? (
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.textMuted,
              fontSize: 22,
              lineHeight: 1,
              padding: '4px 8px',
            }}
            aria-label="Sluiten"
          >
            ×
          </button>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          flexWrap: 'wrap',
          alignItems: 'stretch',
        }}
      >
        {isStaffUI && (
          <div
            style={{
              flex: '1 1 320px',
              minWidth: 280,
              minHeight: 0,
              maxHeight: variant === 'inline' ? 480 : '100%',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              borderRight: `1px solid ${T.border}`,
              padding: '16px 18px 20px',
              background: T.bg,
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                color: T.textMuted,
                textTransform: 'uppercase',
                letterSpacing: 0.75,
                marginBottom: 6,
              }}
            >
              Huidige leden
            </div>
            <div style={{ fontSize: 11, color: T.textSub, marginBottom: 12, lineHeight: 1.35 }}>
              Klik op een lid om rol en beheerdersrechten te wijzigen. Wijzigingen gaan pas na <strong>Opslaan</strong> door.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {members.map((m) => {
                const editable = memberRowEditable(isOwner, isBoard, canInviteMembersHub, m);
                const selectable = canSelectMemberForEdit(m, isOwner, isBoard, canInviteMembersHub);
                const capsResolved =
                  m.role === 'organizer'
                    ? strictCapabilities((m.capabilities || {}) as Partial<EntityMemberCapabilities>)
                    : null;
                const capChips = capsResolved ? CAPABILITY_KEYS.filter((k) => capsResolved[k]).map((k) => CAP_LABELS[k]) : [];
                const busy = managingUid === m.id;
                const isSelected = selectedMemberId === m.id;
                return (
                  <div
                    key={m.id}
                    role={selectable ? 'button' : undefined}
                    tabIndex={selectable ? 0 : undefined}
                    onClick={
                      selectable
                        ? () => {
                            openMemberEditor(m);
                          }
                        : undefined
                    }
                    onKeyDown={
                      selectable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openMemberEditor(m);
                            }
                          }
                        : undefined
                    }
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: '12px 12px',
                      borderRadius: 12,
                      background: T.white,
                      border: isSelected ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                      cursor: selectable ? 'pointer' : 'default',
                      outline: 'none',
                    }}
                  >
                    {m.photoURL ? (
                      <img src={m.photoURL} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: T.accentBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 15,
                          fontWeight: 800,
                          color: T.accent,
                          flexShrink: 0,
                        }}
                      >
                        {(m.name || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{m.name}</span>
                        {m.id === userId && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: T.accent }}>(jij)</span>
                        )}
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 20,
                            background: m.role === 'member' ? T.badgeLight : T.badgeDark,
                            color: m.role === 'member' ? T.text : '#fff',
                          }}
                        >
                          {roleLabelHub(m.role)}
                        </span>
                      </div>
                      {m.email ? (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: T.textMuted,
                            marginTop: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {m.email}
                        </div>
                      ) : null}
                      {m.role === 'organizer' && capChips.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                          {capChips.map((label) => (
                            <span
                              key={label}
                              style={{
                                fontSize: 9.5,
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: 6,
                                background: T.accentLight,
                                color: T.textSub,
                              }}
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                      {m.role === 'organizer' && capChips.length === 0 && capsResolved && (
                        <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 6 }}>
                          Geen actieve beheerdersrechten (alles uitgeschakeld in profiel)
                        </div>
                      )}
                      {editable && (m.role === 'organizer' || m.role === 'member' || m.role === 'va') && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              void removeMemberFromEntity(m);
                            }}
                            style={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              padding: '6px 10px',
                              borderRadius: 8,
                              border: `1.5px solid ${T.red}`,
                              background: T.white,
                              color: T.red,
                              cursor: busy ? 'default' : 'pointer',
                              opacity: busy ? 0.6 : 1,
                            }}
                          >
                            {busy ? '…' : 'Uit groep'}
                          </button>
                        </div>
                      )}
                      {!editable && m.role !== 'owner' && (
                        <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 8 }}>
                          Alleen eigenaar/bestuur past dit lid aan.
                        </div>
                      )}
                    </div>
                    {selectable ? (
                      <span
                        style={{
                          fontSize: 22,
                          fontWeight: 300,
                          color: T.textMuted,
                          lineHeight: 1,
                          alignSelf: 'center',
                          flexShrink: 0,
                          userSelect: 'none',
                        }}
                        aria-hidden
                      >
                        ›
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div
          style={{
            flex: '1 1 400px',
            minWidth: 300,
            minHeight: 0,
            maxHeight: variant === 'inline' ? 'min(78vh, 820px)' : '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            className="scrollbar-always"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollbarGutter: 'stable',
              padding: '16px 18px 22px 22px',
            }}
          >
          {isStaffUI ? (
            selectedMember ? (
              <>
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: T.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.75,
                      marginBottom: 6,
                    }}
                  >
                    Lid bewerken
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{selectedMember.name}</div>
                  {selectedMember.email ? (
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{selectedMember.email}</div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={clearMemberSelection}
                  style={{
                    width: '100%',
                    marginBottom: 18,
                    height: 38,
                    borderRadius: 10,
                    border: `1px solid ${T.border}`,
                    background: T.bg,
                    fontSize: 13,
                    fontWeight: 600,
                    color: T.text,
                    cursor: 'pointer',
                  }}
                >
                  Terug naar uitnodigen
                </button>

                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: T.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    marginBottom: 10,
                  }}
                >
                  Rol & rechten
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: showBoardVaInEditor ? 10 : 12, flexWrap: 'wrap' }}>
                  {(['member', 'organizer'] as const).map((r) => {
                    const sel = editRole === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => pickEditRole(r)}
                        style={{
                          flex: '1 1 140px',
                          minHeight: 48,
                          borderRadius: 12,
                          border: `1.5px solid ${sel ? T.accent : T.border}`,
                          background: sel ? T.accent : T.white,
                          fontSize: 13.5,
                          fontWeight: sel ? 700 : 600,
                          color: sel ? '#fff' : T.text,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          padding: '10px 8px',
                          lineHeight: 1.25,
                        }}
                      >
                        {r === 'member' ? (
                          'Lid'
                        ) : (
                          <span>
                            Beheerder
                            <span
                              style={{
                                display: 'block',
                                fontSize: 10.5,
                                fontWeight: 600,
                                opacity: sel ? 0.9 : 0.75,
                                marginTop: 2,
                              }}
                            >
                              Kies rechten hieronder
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {showBoardVaInEditor ? (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => pickEditRole('board')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 10,
                        border: `1.5px solid ${editRole === 'board' ? T.accent : T.border}`,
                        background: editRole === 'board' ? T.accent : T.white,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: editRole === 'board' ? '#fff' : T.text,
                        cursor: 'pointer',
                      }}
                    >
                      Bestuur
                    </button>
                    <button
                      type="button"
                      onClick={() => pickEditRole('va')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 10,
                        border: `1.5px solid ${editRole === 'va' ? T.accent : T.border}`,
                        background: editRole === 'va' ? T.accent : T.white,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: editRole === 'va' ? '#fff' : T.text,
                        cursor: 'pointer',
                      }}
                    >
                      VA
                    </button>
                  </div>
                ) : null}

                {editRole === 'organizer' && (
                  <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 12, border: `1px solid ${T.border}`, background: T.bg }}>
                    {rightsBlockHeader}
                    {CAPABILITY_ROWS.map(([key, title, sub]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editCapabilities[key]}
                          onChange={() => toggleEditCapability(key)}
                          style={{ width: 18, height: 18, marginTop: 2, accentColor: T.accent }}
                        />
                        <span style={{ flex: 1 }}>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.text }}>{title}</span>
                          <span style={{ display: 'block', fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{sub}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={clearMemberSelection}
                    style={{
                      flex: 1,
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${T.border}`,
                      background: T.white,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: T.text,
                      cursor: 'pointer',
                    }}
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={() => void saveMemberEdits()}
                    disabled={!isMemberEditDirty || editSaving}
                    style={{
                      flex: 1,
                      height: 44,
                      borderRadius: 12,
                      border: 'none',
                      background: !isMemberEditDirty || editSaving ? '#BABABA' : T.accent,
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: '#fff',
                      cursor: !isMemberEditDirty || editSaving ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    {editSaving ? (
                      'Opslaan…'
                    ) : (
                      <>
                        <CheckIcon size={14} weight="bold" /> Opslaan
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
            <>
              {renderQuickInvite({ marginBottom: 18 })}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  margin: '0 0 18px',
                  color: T.textMuted,
                  fontSize: 11.5,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                }}
              >
                <div style={{ flex: 1, height: 1, background: T.border }} />
                Gericht uitnodigen
                <div style={{ flex: 1, height: 1, background: T.border }} />
              </div>

              <div style={{ background: T.bg, borderRadius: 12, padding: '10px 0', textAlign: 'center', marginBottom: 18 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FF5C5C', display: 'inline-block' }} />
                  Gast
                </span>
              </div>

              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: T.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  marginBottom: 8,
                }}
              >
                Zoek op gebruikersnaam of naam
              </div>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => void handleSearch(e.target.value)}
                  placeholder="Naam zoeken..."
                  style={{
                    width: '100%',
                    height: 44,
                    padding: '0 14px',
                    borderRadius: 12,
                    border: `1.5px solid ${T.border}`,
                    fontSize: 13.5,
                    outline: 'none',
                    boxSizing: 'border-box',
                    color: T.text,
                  }}
                />
                {searching && <Loader size={14} strokeWidth={2} style={{ position: 'absolute', right: 12, top: 15 }} />}
              </div>
              {searchResults.length > 0 && (
                <div style={{ borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden', marginBottom: 16 }}>
                  {searchResults.map((u) => (
                    <div
                      key={u.userId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        borderBottom: `1px solid ${T.borderLight}`,
                        background: T.white,
                      }}
                    >
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: T.accentBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 800,
                          color: T.accent,
                          flexShrink: 0,
                        }}
                      >
                        {(u.displayName || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{u.displayName}</div>
                        {u.email && <div style={{ fontSize: 11.5, color: T.textMuted }}>{u.email}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const invRef = await addDoc(collection(db, 'entityInvitations'), {
                            entityId,
                            clubId,
                            clubName,
                            entityName: clubName,
                            entityType,
                            userId: u.userId,
                            role,
                            ...(role === 'organizer' ? { capabilities } : {}),
                            status: 'pending',
                            invitedBy: userId,
                            createdAt: serverTimestamp(),
                          });
                          await addDoc(collection(db, 'inboxItems'), {
                            userId: u.userId,
                            type: 'group_invitation',
                            category: 'system',
                            priority: 'high',
                            title: `Uitnodiging voor ${clubName}`,
                            message: `${userName} nodigt je uit als ${inviteTargetLabel(role)} van ${clubName}.`,
                            isRead: false,
                            createdAt: serverTimestamp(),
                            actionData: {
                              actionType: 'group_invitation',
                              invitationId: invRef.id,
                              entityId,
                              entityName: clubName,
                              entityType,
                              role,
                              invitedBy: userId,
                              inviterName: userName,
                            },
                          });
                          setSearch('');
                          setSearchResults([]);
                        }}
                        style={{ ...S.accentBtn, height: 30, fontSize: 12 }}
                      >
                        Toevoegen
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  margin: '4px 0 16px',
                  color: T.textMuted,
                  fontSize: 11.5,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                }}
              >
                <div style={{ flex: 1, height: 1, background: T.border }} />
                Of via e-mail / telefoon
                <div style={{ flex: 1, height: 1, background: T.border }} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-mailadres"
                  style={{
                    width: '100%',
                    height: 44,
                    padding: '0 14px',
                    borderRadius: 12,
                    border: `1.5px solid ${T.border}`,
                    fontSize: 13.5,
                    outline: 'none',
                    color: T.text,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: showRoleExtras ? 10 : 12, flexWrap: 'wrap' }}>
                {(['member', 'organizer'] as const).map((r) => {
                  const sel = role === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      style={{
                        flex: '1 1 140px',
                        minHeight: 48,
                        borderRadius: 12,
                        border: `1.5px solid ${sel ? T.accent : T.border}`,
                        background: sel ? T.accent : T.white,
                        fontSize: 13.5,
                        fontWeight: sel ? 700 : 600,
                        color: sel ? '#fff' : T.text,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        padding: '10px 8px',
                        lineHeight: 1.25,
                      }}
                    >
                      {r === 'member' ? (
                        'Lid'
                      ) : (
                        <span>
                          Beheerder
                          <span
                            style={{
                              display: 'block',
                              fontSize: 10.5,
                              fontWeight: 600,
                              opacity: sel ? 0.9 : 0.75,
                              marginTop: 2,
                            }}
                          >
                            Kies rechten hieronder
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {showRoleExtras ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {extras.includes('board') && (
                    <button
                      type="button"
                      onClick={() => setRole('board')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 10,
                        border: `1.5px solid ${role === 'board' ? T.accent : T.border}`,
                        background: role === 'board' ? T.accent : T.white,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: role === 'board' ? '#fff' : T.text,
                        cursor: 'pointer',
                      }}
                    >
                      Bestuur
                    </button>
                  )}
                  {extras.includes('va') && (
                    <button
                      type="button"
                      onClick={() => setRole('va')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 10,
                        border: `1.5px solid ${role === 'va' ? T.accent : T.border}`,
                        background: role === 'va' ? T.accent : T.white,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: role === 'va' ? '#fff' : T.text,
                        cursor: 'pointer',
                      }}
                    >
                      VA
                    </button>
                  )}
                </div>
              ) : null}

              {role === 'organizer' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 12, border: `1px solid ${T.border}`, background: T.bg }}>
                  {rightsBlockHeader}
                  {CAPABILITY_ROWS.map(([key, title, sub]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={capabilities[key]}
                        onChange={() => toggleCapability(key)}
                        style={{ width: 18, height: 18, marginTop: 2, accentColor: T.accent }}
                      />
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.text }}>{title}</span>
                        <span style={{ display: 'block', fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{sub}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                {variant === 'modal' && onClose ? (
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      flex: 1,
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${T.border}`,
                      background: T.white,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: T.text,
                      cursor: 'pointer',
                    }}
                  >
                    Annuleren
                  </button>
                ) : (
                  <div style={{ flex: 1 }} />
                )}
                <button
                  type="button"
                  onClick={() => void sendInvite()}
                  disabled={sending || !email.trim()}
                  style={{
                    flex: 1,
                    height: 44,
                    borderRadius: 12,
                    border: 'none',
                    background: sent ? T.green : sending || !email.trim() ? '#BABABA' : T.accent,
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: '#fff',
                    cursor: sending || !email.trim() ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  {sent ? (
                    <>
                      <CheckIcon size={14} weight="bold" /> Verstuurd
                    </>
                  ) : sending ? (
                    'Versturen…'
                  ) : (
                    <>
                      <MailIcon size={14} weight="regular" /> Uitnodiging sturen
                    </>
                  )}
                </button>
              </div>
            </>
            )
          ) : (
            <>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 6 }}>Nodig iemand uit</div>
                <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5 }}>
                  Deel deze link met iemand die past bij jouw netwerk
                </div>
              </div>

              {renderQuickInvite({ marginBottom: 20 })}

              {variant === 'modal' && onClose ? (
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    width: '100%',
                    height: 44,
                    borderRadius: 12,
                    border: `1.5px solid ${T.border}`,
                    background: T.white,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: T.text,
                    cursor: 'pointer',
                  }}
                >
                  Sluiten
                </button>
              ) : null}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );

  if (variant === 'inline') {
    return (
      <>
        <div style={{ width: '100%' }}>{inner}</div>
        {hubGuidePanel}
      </>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
    >
      {inner}
      {hubGuidePanel}
    </div>
  );
};
